import StickyTableResponsive from '../../../components/common/StickyTableResponsive';
import SortableTh from '../../../components/common/SortableTh';
import React, { useCallback, useEffect, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import { Link } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppButton,
  AppEmptyState,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import secteursAdminService from '../../../services/secteursAdminService';
import { notify } from '../../../components/common/AppToast';
import { formatDashboardCa, formatDashboardNumber } from '../../../utils/dashboardV2Utils';
import { ROUTES } from '../../../config/routes';
import { getSocieteBrand } from '../../../config/societeBrand';

const TABLE_SORT_COLUMNS = {
  produit: { type: 'string', getValue: (row) => row.label || row.product_label || row.productLabel },
  societe: { type: 'string', getValue: (row) => row.societe },
  ca: { type: 'number', getValue: (row) => row.ca_total ?? row.ca ?? 0 },
  clients: { type: 'number', getValue: (row) => row.nb_clients ?? row.clients ?? 0 },
};

const SecteursAdminProduitsPage = () => {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(produits, TABLE_SORT_COLUMNS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await secteursAdminService.getProduits(annee);
      setProduits(data.produits ?? []);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [annee]);

  useEffect(() => { load(); }, [load]);

  return (
    <MasterLayout>
      <Breadcrumb
        title="Produits par secteur"
        subtitle="Répartition CA par produit et secteur géographique"
        actions={(
          <div className="d-flex gap-2 align-items-center">
            <AppButton as={Link} to={ROUTES.secteurs.admin} size="sm" variant="outline-secondary">Retour</AppButton>
            <AppSelect
              size="sm"
              className="w-auto"
              value={annee}
              onChange={(e) => setAnnee(Number(e.target.value))}
            >
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </AppSelect>
          </div>
        )}
      />

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : produits.length === 0 ? (
            <AppEmptyState message={`Aucun produit pour ${annee}`} />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="produit" label="Produit" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="societe" label="Société" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="ca" label="CA total" className="text-end" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="clients" label="Clients" className="text-end" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{p.label || p.product_label || p.productLabel || '—'}</strong>
                        {(p.ref || p.product_ref) && <small className="d-block text-muted">{p.ref || p.product_ref}</small>}
                      </td>
                      <td><span className={`badge ${getSocieteBrand(p.societe).badgeClass}`}>{p.societe || '—'}</span></td>
                      <td className="text-end">{formatDashboardCa(p.ca_total ?? p.ca ?? 0)}</td>
                      <td className="text-end">{formatDashboardNumber(p.nb_clients ?? p.clients ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default SecteursAdminProduitsPage;
